import { useCallback, useEffect, useRef } from "react"
import { jsPlumb } from "jsplumb"

const RIGHT_DIV_ID = "train_right-small-div"
const LEFT_DIV_PREFIX = "train_left-div-"
const MAX_LAYOUT_RETRIES = 20

export default function useJsPlumbConnections(
  classes,
  currentModelId,
  leftContainerRef,
  connectionContainerRef,
  jsPlumbInstance,
  mainTab,
  layoutKey
) {
  const frameRef = useRef(null)
  const repaintFrameRef = useRef(null)
  const retriesRef = useRef(0)
  const wiredIdsRef = useRef([])
  const resizeObserverRef = useRef(null)
  const syncRef = useRef(() => {})

  const classIds = Object.keys(classes || {})
    .map((key) => classes[key]?.id)
    .filter((id) => id !== undefined && id !== null)
  // Identity of `classes` changes on every render (it is rebuilt by filtering),
  // so effects key off the id list instead of the object.
  const signature = classIds.join("|")
  const classIdsRef = useRef(classIds)
  classIdsRef.current = classIds

  const cancelFrames = useCallback(() => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current)
      frameRef.current = null
    }
    if (repaintFrameRef.current !== null) {
      cancelAnimationFrame(repaintFrameRef.current)
      repaintFrameRef.current = null
    }
  }, [])

  // First frame lets React commit the new DOM, second lets the browser settle
  // layout, so jsPlumb measures final positions instead of the old ones.
  const runAfterLayout = useCallback((fn) => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null
        fn()
      })
    })
  }, [])

  const repaint = useCallback(() => {
    if (repaintFrameRef.current !== null) return
    repaintFrameRef.current = requestAnimationFrame(() => {
      repaintFrameRef.current = null
      const instance = jsPlumbInstance.current
      if (!instance) return
      try {
        instance.repaintEverything()
      } catch (e) {
        console.error("[jsPlumb] repaint failed:", e)
      }
    })
  }, [jsPlumbInstance])

  const observeLayout = useCallback(() => {
    if (typeof ResizeObserver === "undefined") return

    resizeObserverRef.current?.disconnect()

    const observer = new ResizeObserver(() => repaint())
    const targets = [RIGHT_DIV_ID, ...wiredIdsRef.current]
      .map((id) => document.getElementById(id))
      .filter(Boolean)

    if (leftContainerRef.current) targets.push(leftContainerRef.current)
    if (connectionContainerRef.current) {
      // The wrapper resizes when the right panel swaps its content height.
      targets.push(connectionContainerRef.current.parentElement)
    }

    targets.filter(Boolean).forEach((el) => observer.observe(el))
    resizeObserverRef.current = observer
  }, [repaint, leftContainerRef, connectionContainerRef])

  const syncConnections = useCallback(() => {
    const instance = jsPlumbInstance.current
    if (!instance) return
    if (!connectionContainerRef.current) return

    const rightEl = document.getElementById(RIGHT_DIV_ID)
    const targetIds = classIdsRef.current.map((id) => `${LEFT_DIV_PREFIX}${id}`)
    const presentIds = targetIds.filter((id) => document.getElementById(id))

    // Both panels re-render on a model-type switch and they do not land in the
    // same frame; wait until every wire has both of its ends in the DOM.
    if (!rightEl || presentIds.length !== targetIds.length) {
      if (retriesRef.current < MAX_LAYOUT_RETRIES) {
        retriesRef.current += 1
        runAfterLayout(() => syncRef.current())
        return
      }
    }
    retriesRef.current = 0

    if (!rightEl) {
      instance.deleteEveryEndpoint()
      wiredIdsRef.current = []
      return
    }

    instance.batch(() => {
      // Endpoints cache both the DOM node and its measured offset. After a
      // re-render those nodes are detached, so tear the whole graph down
      // (deleteEveryEndpoint also clears the managed-element/offset caches)
      // before rewiring against the current DOM.
      instance.deleteEveryEndpoint()

      presentIds.forEach((targetId) => {
        instance.connect({
          detachable: false,
          source: RIGHT_DIV_ID,
          target: targetId,
          anchors: ["Left", "Right"],
        })
      })
    })

    wiredIdsRef.current = presentIds
    observeLayout()
    repaint()
  }, [
    runAfterLayout,
    observeLayout,
    repaint,
    jsPlumbInstance,
    connectionContainerRef,
  ])

  syncRef.current = syncConnections

  // Destroy jsPlumb whenever we leave the train tab
  useEffect(() => {
    if (mainTab !== "train") {
      cancelFrames()
      resizeObserverRef.current?.disconnect()
      resizeObserverRef.current = null
      wiredIdsRef.current = []
      if (jsPlumbInstance.current) {
        jsPlumbInstance.current.reset()
        jsPlumbInstance.current = null
      }
    }
  }, [mainTab, cancelFrames, jsPlumbInstance])

  // Initialize jsPlumb when on train tab and container is ready
  useEffect(() => {
    if (mainTab !== "train") return
    if (!connectionContainerRef.current) return

    jsPlumbInstance.current = jsPlumb.getInstance({
      paintStyle: { stroke: "#000", strokeWidth: 2 },
      endpoint: ["Dot", { radius: 4 }],
      connector: ["Bezier", { curviness: 100 }],
      container: connectionContainerRef.current,
    })

    return () => {
      cancelFrames()
      resizeObserverRef.current?.disconnect()
      resizeObserverRef.current = null
      wiredIdsRef.current = []
      if (jsPlumbInstance.current) {
        jsPlumbInstance.current.reset()
        jsPlumbInstance.current = null
      }
    }
  }, [
    currentModelId,
    mainTab,
    cancelFrames,
    jsPlumbInstance,
    connectionContainerRef,
  ])

  // Redraw whenever the visible class set, the model, the tab or the layout
  // (model-type toggle) changes
  useEffect(() => {
    if (mainTab !== "train") return
    if (!jsPlumbInstance.current) return

    retriesRef.current = 0
    runAfterLayout(() => syncRef.current())

    return () => cancelFrames()
  }, [
    signature,
    currentModelId,
    mainTab,
    layoutKey,
    runAfterLayout,
    cancelFrames,
    jsPlumbInstance,
  ])

  // Fonts, thumbnails and webcam previews settle after the first paint and
  // shift the rows they live in; repaint once the browser reports them.
  useEffect(() => {
    if (mainTab !== "train") return

    const handleLayoutChange = () => repaint()
    const leftContainer = leftContainerRef.current

    leftContainer?.addEventListener("scroll", handleLayoutChange)
    window.addEventListener("resize", handleLayoutChange)
    window.addEventListener("transitionend", handleLayoutChange, true)

    return () => {
      leftContainer?.removeEventListener("scroll", handleLayoutChange)
      window.removeEventListener("resize", handleLayoutChange)
      window.removeEventListener("transitionend", handleLayoutChange, true)
    }
  }, [mainTab, repaint, leftContainerRef])
}
